/**
 * La ficha de una cita, en un diálogo.
 *
 * Antes era una pantalla entera con seis tarjetas: cliente, reserva, pago,
 * estado, respuestas y nota, cada una con su icono, su título y su subtítulo. La
 * mayoría de las veces se abre para hacer UNA cosa —ver a qué hora es, marcar
 * que asistió, copiar un teléfono— y para eso había que perder la lista, cargar
 * otra pantalla y leer cuatro encabezados que ya se sabían.
 *
 * Ahora se abre encima: la lista sigue detrás y cerrar devuelve exactamente a
 * donde se estaba. El ancho permite leer cliente y pago en paralelo, mientras
 * respuestas y nota conservan todo el renglón para contenido largo.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTabThumb } from '../../../hooks/useTabThumb'
import { createPortal } from 'react-dom'
import {
  IconAlertTriangle,
  IconClipboardList,
  IconHistory,
  IconNote,
  IconCalendarEvent,
  IconCalendarPlus,
  IconCalendarCheck,
  IconCalendarTime,
  IconChevronsRight,
  IconArrowNarrowRight,
  IconCopy,
  IconCreditCardOff,
  IconCreditCard,
  IconArrowBackUp,
  IconDots,
  IconHourglass,
  IconMail,
  IconPhone,
  IconCheck,
  IconLoader2,
  IconPencil,
  IconReceipt,
  IconSend,
  IconTrash,
  IconUser,
  IconUserCheck,
  IconUserOff,
  IconX, IconCalendarOff } from '@tabler/icons-react'

import { longDate, weekdayIn } from '../../../lib/dates'
import { OrderDetailDialog } from './OrderDetailDialog'
import { BookingStatusSelect } from './BookingStatusSelect'
import { orderStatusTone } from './offerShared'
import { useToast } from '../../Toast'
import { createBookingNote, deleteBookingNote, getOrder, listBookingEvents, listBookingNotes, listBookingSlots, updateBooking, updateBookingNote, type Booking, type BookingEvent, type BookingResource, type BookingNote, type BookingSlot, type OrderDetail } from '../../../lib/api'
import { allDayCount, bookingError, isPast, longDurationLabel, prettyPhone, timeIn } from './bookingShared'
import type { Translate } from '@webbuilder/i18n'
import { activeLanguage, useI18n } from '../../../lib/i18n'
import { NOTE_TEXT_MAX } from '../../../lib/projectNotes'
import { COMMAND_KEY, NoteAvatar, relativeNoteTime } from '../../../lib/noteFeed'
import { SlotsEmpty } from './SlotsEmpty'

/** La hora suelta de la bitácora, en el idioma de quien mira. */
function clockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(activeLanguage(), {
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  })
}

function paymentStatus(order: OrderDetail, t: Translate): string {
  if (order.status === 'refunded') return t('bookings.detail.refunded')
  if (order.status === 'partially_refunded') return t('bookings.detail.partiallyRefunded')
  return t('bookings.detail.paid')
}

/** La fecha que ve el negocio en la zona de la cita, para el selector nativo. */
function dayIn(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date(iso))
}

/**
 * Cambiar una cita no es escribir una fecha cualquiera: se elige un hueco que
 * el servidor acaba de calcular. Así el equipo puede atender una llamada sin
 * crear choques ni poner a alguien fuera del horario del servicio.
 */
function RescheduleBookingDialog({ projectId, token, booking, onClose, onRescheduled }: {
  projectId: string
  token: string
  booking: Booking
  onClose: () => void
  onRescheduled: (booking: Booking) => void
}) {
  const { t } = useI18n()
  const [day, setDay] = useState(() => dayIn(booking.startsAt, booking.timezone))
  const [slot, setSlot] = useState('')
  const [slots, setSlots] = useState<BookingSlot[]>([])
  const [slotTimezone, setSlotTimezone] = useState(booking.timezone)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !saving) onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, saving])

  useEffect(() => {
    let active = true
    setSlot('')
    setLoading(true)
    setError('')
    listBookingSlots(projectId, { offerId: booking.offerId!, from: day, to: day }, token)
      .then((result) => {
        if (!active) return
        setSlots(result.slots)
        setSlotTimezone(result.timezone)
      })
      .catch((cause: unknown) => { if (active) setError(bookingError(cause)) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [projectId, token, booking.offerId, day])

  const save = async () => {
    if (!slot || saving) return
    setSaving(true)
    setError('')
    try {
      const duration = Date.parse(booking.endsAt) - Date.parse(booking.startsAt)
      const updated = await updateBooking(projectId, booking.id, {
        startsAt: slot,
        endsAt: new Date(Date.parse(slot) + duration).toISOString(),
      }, token)
      onRescheduled(updated)
      onClose()
    } catch (cause) {
      setError(bookingError(cause))
    } finally {
      setSaving(false)
    }
  }

  return createPortal(
    <div className="dash-modal-backdrop dash-bk-reschedule-backdrop" onPointerDown={(event) => { if (event.target === event.currentTarget && !saving) onClose() }}>
      <section className="dash-bk-reschedule-dialog" role="dialog" aria-modal="true" aria-labelledby="booking-reschedule-title">
        <header>
          <div className="dash-bk-reschedule-head-copy">
            <span className="dash-widget-glass-icon dash-bk-reschedule-icon" aria-hidden="true"><IconCalendarTime size={16} /></span>
            <div>
              <h2 id="booking-reschedule-title">{t('bookings.detail.rescheduleTitle')}</h2>
              <p>{t('bookings.detail.rescheduleBody')}</p>
            </div>
          </div>
          <button type="button" className="dash-create-close" aria-label={t('common.close')} disabled={saving} onClick={onClose}><IconX size={16} /></button>
        </header>
        <div className="dash-bk-reschedule-body">
          <label className="dash-new-booking-field">
            <span>{t('bookings.detail.rescheduleDay')}</span>
            <input
              type="date"
              value={day}
              disabled={saving}
              onChange={(event) => setDay(event.target.value)}
              onClick={(event) => { try { event.currentTarget.showPicker() } catch { /* Se puede escribir. */ } }}
            />
          </label>
          <div className="dash-new-booking-field">
            <span>{t('bookings.detail.rescheduleTime')} <small>{slotTimezone}</small></span>
            {loading ? (
              <p className="dash-new-booking-empty"><IconLoader2 className="dash-spin" size={15} /> {t('bookings.new.searching')}</p>
            ) : error ? (
              <p className="dash-contact-add-error" role="alert">{error}</p>
            ) : slots.length === 0 ? (
              <SlotsEmpty icon={<IconCalendarOff size={20} />} title={t('bookings.new.noSlotsTitle')} body={t('bookings.new.noSlotsBody')} />
            ) : (
              <div className="dash-new-booking-slots" aria-label={t('bookings.new.slotsAria')}>
                {slots.map((option) => (
                  <button
                    key={option.startsAt}
                    type="button"
                    className={option.startsAt === slot ? 'is-selected' : ''}
                    aria-pressed={option.startsAt === slot}
                    disabled={saving}
                    onClick={() => setSlot(option.startsAt)}
                  >
                    <strong>{timeIn(option.startsAt, slotTimezone)}</strong>
                    {option.seatsLeft > 1 ? <small>{t('bookings.new.seatsLeft', { count: option.seatsLeft })}</small> : null}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <footer>
          <button type="button" className="dash-secondary-btn" disabled={saving} onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="dash-primary-btn" disabled={!slot || saving} onClick={() => { void save() }}>
            {saving ? <IconLoader2 className="dash-spin" size={14} /> : <IconCalendarEvent size={14} />}
            {t('bookings.detail.rescheduleConfirm')}
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  )
}

/**
 * Un apartado con su título.
 *
 * Estuvo plegado y fue peor: para saber si alguien había dejado teléfono había
 * que abrir «Cliente», y para ver si la nota decía algo, abrir «Nota». Son cinco
 * datos por apartado, no cincuenta; caben todos a la vista y lo que hace falta
 * es que se distingan, no que se escondan. De eso se encarga el título.
 */
function Block({ title, icon, wide = false, variant, aside, children }: {
  title: string
  icon: React.ReactNode
  wide?: boolean
  variant?: 'client' | 'payment' | 'answers'
  aside?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className={`dash-bk-block${wide ? ' is-wide' : ''}${variant ? ` is-${variant}` : ''}`}>
      <header>
        <span className="dash-bk-block-title">
          <span className="dash-widget-glass-icon dash-settings-head-icon dash-bk-block-icon" aria-hidden="true">{icon}</span>
          <h3>{title}</h3>
        </span>
        {aside}
      </header>
      {children}
    </section>
  )
}

/** Una línea de dato. Sin icono: doce iconos en una columna son ruido, no ayuda. */
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="dash-bk-row"><dt>{label}</dt><dd>{children}</dd></div>
}

function ArtistFace({ person }: { person: Pick<BookingResource, 'name' | 'photoUrl'> }) {
  return (
    <span className="dash-booking-artist-face" aria-hidden="true">
      {person.photoUrl ? <img src={person.photoUrl} alt="" /> : person.name.trim().split(/\s+/).slice(0, 2).map((part) => part[0] ?? '').join('').toUpperCase()}
    </span>
  )
}

/**
 * Con quién es la cita, y el cambio a otra persona del equipo.
 *
 * Se cambia en el sitio, sin diálogo: es la misma hora, sólo otra persona. El
 * servidor comprueba que esa persona esté libre a esa hora (409 si no) y lo
 * apunta en el historial.
 */
function ArtistPicker({ team, currentId, disabled, onPick }: {
  team: BookingResource[]
  currentId: string
  disabled: boolean
  onPick: (resourceId: string) => void
}) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const current = team.find((row) => row.id === currentId)
  /* Las inactivas no se ofrecen, pero la actual se enseña aunque lo esté. */
  const options = team.filter((row) => row.active && row.id !== currentId)
  return (
    <div className="dash-bk-artist">
      <div className="dash-bk-artist-current">
        {current ? <ArtistFace person={current} /> : null}
        <span>{current?.name ?? '—'}</span>
        {options.length && !disabled ? (
          <button type="button" className="dash-bk-artist-toggle" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
            {t(open ? 'bookings.detail.artistDone' : 'bookings.detail.artistChange')}
          </button>
        ) : null}
      </div>
      {open && !disabled ? (
        <ul className="dash-bk-artist-options">
          {options.map((person) => (
            <li key={person.id}>
              <button type="button" onClick={() => { setOpen(false); onPick(person.id) }}>
                <ArtistFace person={person} />
                <span>{person.name}{person.role ? <small>{person.role}</small> : null}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/** Cómo se lee cada suceso de la bitácora. */
const EVENT_KEY: Record<string, string> = {
  created: 'bookings.event.created',
  held: 'bookings.event.held',
  paid: 'bookings.event.paid',
  confirmed: 'bookings.event.confirmed',
  rescheduled: 'bookings.event.rescheduled',
  cancelled: 'bookings.event.cancelled',
  completed: 'bookings.event.completed',
  no_show: 'bookings.event.no_show',
  reassigned: 'bookings.event.reassigned',
  refunded: 'bookings.event.refunded',
  refund_pending: 'bookings.event.refundPending',
}

const EVENT_ICON: Record<string, typeof IconCalendarEvent> = {
  created: IconCalendarPlus,
  held: IconHourglass,
  paid: IconCreditCard,
  confirmed: IconCalendarCheck,
  rescheduled: IconCalendarTime,
  cancelled: IconCalendarOff,
  completed: IconUserCheck,
  no_show: IconUserOff,
  reassigned: IconUser,
  refunded: IconArrowBackUp,
  refund_pending: IconHourglass,
}

/**
 * El historial de la cita, en orden.
 *
 * Se pide al abrir la pestaña y no con la ficha: casi nadie la abre, y una
 * consulta más por cada cita que se mira es una consulta que casi siempre sobra.
 *
 * Lo reconstruido —lo que se deduce de la propia fila para las citas anteriores
 * a la bitácora— va marcado. No es lo mismo saber que algo pasó porque quedó
 * anotado que deducirlo de una fecha suelta, y presentarlos igual sería afirmar
 * de más.
 */
function BookingTimeline({ projectId, token, bookingId }: {
  projectId: string
  token: string
  bookingId: string
}) {
  const { t } = useI18n()
  const [rows, setRows] = useState<BookingEvent[] | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let alive = true
    setRows(null)
    setFailed(false)
    listBookingEvents(projectId, bookingId, token)
      .then((data) => { if (alive) setRows(data) })
      .catch(() => { if (alive) setFailed(true) })
    return () => { alive = false }
  }, [projectId, bookingId, token])

  if (failed) return <p className="dash-bk-muted">{t('bookings.timeline.failed')}</p>
  if (!rows) return <p className="dash-bk-muted"><IconLoader2 className="dash-spin" size={15} /> {t('bookings.timeline.loading')}</p>
  if (rows.length === 0) return <p className="dash-bk-muted">{t('bookings.timeline.empty')}</p>

  return (
    <ol className="dash-bk-timeline">
      {rows.map((row) => {
        const EventIcon = EVENT_ICON[row.kind] ?? IconHistory
        const quien = row.actorName
          ? row.actorName
          : row.actorKind === 'customer' ? t('bookings.timeline.actor.customer')
          : row.actorKind === 'team' ? t('bookings.timeline.actor.team')
          : t('bookings.timeline.actor.auto')
        const desde = typeof row.meta.from === 'string' ? row.meta.from : ''
        const hasta = typeof row.meta.to === 'string' ? row.meta.to : ''
        return (
          <li key={row.id} className={row.meta.reconstructed ? 'is-guessed' : ''}>
            <span className="dash-bk-timeline-dot" aria-hidden="true" />
            <div className="dash-bk-timeline-copy">
              <strong className="dash-bk-timeline-title"><EventIcon size={16} stroke={1.8} aria-hidden="true" /><span>{t(EVENT_KEY[row.kind] ?? row.kind)}</span></strong>
              {/* De dónde a dónde, con la misma flecha que la actividad del
                  contacto. Antes sólo se decía la hora anterior —«Antes: …»— y
                  había que mirar la ficha de arriba para saber a cuál se movió.
                  Las líneas viejas no guardaron el destino: ésas siguen
                  enseñando sólo el origen, que es lo único que hay. */}
              {desde && row.kind === 'rescheduled' ? (
                hasta ? (
                  <small className="dash-bk-timeline-change">
                    <span className="dash-bk-timeline-from">{longDate(desde)} · {clockTime(desde)}</span>
                    <IconArrowNarrowRight size={14} aria-hidden="true" />
                    <span className="dash-bk-timeline-to">{longDate(hasta)} · {clockTime(hasta)}</span>
                  </small>
                ) : (
                  <small>{t('bookings.timeline.before', { when: `${longDate(desde)} · ${clockTime(desde)}` })}</small>
                )
              ) : null}
              {/* El cambio de artista: de quién a quién, con nombres congelados
                  al cambiar (renombrar a alguien no reescribe la bitácora). */}
              {row.kind === 'reassigned' && (desde || hasta) ? (
                <small className="dash-bk-timeline-change">
                  <span className="dash-bk-timeline-from">{desde || '—'}</span>
                  <IconArrowNarrowRight size={14} aria-hidden="true" />
                  <span className="dash-bk-timeline-to">{hasta || '—'}</span>
                </small>
              ) : null}
              {/* El reembolso automático: cuánto, y si quedó pendiente, por qué. */}
              {(row.kind === 'refunded' || row.kind === 'refund_pending') && typeof row.meta.amount === 'number' ? (
                <small>{new Intl.NumberFormat(activeLanguage(), { style: 'currency', currency: (typeof row.meta.currency === 'string' && row.meta.currency ? row.meta.currency : 'usd').toUpperCase() }).format(row.meta.amount / 100)}{row.kind === 'refund_pending' ? ` · ${t('bookings.event.refundRetry')}` : ''}</small>
              ) : null}
              {typeof row.meta.reason === 'string' && row.meta.reason ? <small>«{row.meta.reason}»</small> : null}
              <span className="dash-bk-timeline-meta">
                {t('bookings.timeline.meta', { when: `${longDate(row.at)} · ${clockTime(row.at)}`, who: quien })}
                {row.meta.reconstructed ? t('bookings.timeline.guessed') : ''}
              </span>
            </div>
          </li>
        )
      })}
    </ol>
  )
}

/** Hilo interno de la reserva: cada comentario conserva autor, foto y fecha. */
function BookingNotes({ projectId, token, bookingId }: {
  projectId: string
  token: string
  bookingId: string
}) {
  const { t } = useI18n()
  const [notes, setNotes] = useState<BookingNote[] | null>(null)
  const [canWrite, setCanWrite] = useState(false)
  const [draft, setDraft] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let alive = true
    setNotes(null)
    setFailed(false)
    listBookingNotes(projectId, bookingId, token)
      .then((result) => {
        if (!alive) return
        setNotes(result.notes)
        setCanWrite(result.canWrite)
      })
      .catch(() => { if (alive) setFailed(true) })
    return () => { alive = false }
  }, [bookingId, projectId, token])

  const add = async () => {
    const body = draft.trim().slice(0, NOTE_TEXT_MAX)
    if (!body || saving) return
    setSaving(true)
    setFailed(false)
    try {
      const created = await createBookingNote(projectId, bookingId, body, token)
      setNotes((current) => [created, ...(current ?? [])])
      setDraft('')
    } catch {
      setFailed(true)
    } finally {
      setSaving(false)
    }
  }

  const saveEdit = async (noteId: string) => {
    const body = editText.trim().slice(0, NOTE_TEXT_MAX)
    if (!body || saving) return
    setSaving(true)
    setFailed(false)
    try {
      const updated = await updateBookingNote(projectId, bookingId, noteId, body, token)
      setNotes((current) => current?.map((note) => note.id === noteId ? updated : note) ?? [])
      setEditingId(null)
    } catch {
      setFailed(true)
    } finally {
      setSaving(false)
    }
  }

  const remove = async (noteId: string) => {
    if (saving) return
    setSaving(true)
    setFailed(false)
    try {
      await deleteBookingNote(projectId, bookingId, noteId, token)
      setNotes((current) => current?.filter((note) => note.id !== noteId) ?? [])
      setDeletingId(null)
    } catch {
      setFailed(true)
    } finally {
      setSaving(false)
    }
  }

  if (notes === null && !failed) {
    return <p className="dash-bk-muted"><IconLoader2 className="dash-spin" size={15} /> {t('bookings.notes.loading')}</p>
  }

  return (
    <div className="dash-notes-feed-widget dash-bk-notes-feed">
      {canWrite ? (
        <div className={`dash-notes-composer${draft.trim() ? ' has-draft' : ''}`}>
          <div className="dash-notes-composer-row">
            <div className="dash-notes-composer-input-box">
              <textarea
                className="dash-notes-composer-textarea"
                placeholder={t('bookings.detail.notePlaceholder')}
                value={draft}
                maxLength={NOTE_TEXT_MAX}
                rows={1}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                    event.preventDefault()
                    void add()
                  }
                }}
              />
              <div className="dash-notes-composer-bar">
                <span className="dash-notes-kbd-hint">{t('overview.notes.keyboardHint', { key: COMMAND_KEY })}</span>
                <button type="button" className="dash-notes-submit-btn" disabled={!draft.trim() || saving} onClick={() => { void add() }}>
                  <IconSend size={13} /> {t('overview.notes.add')}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {failed ? <div className="dash-notes-error-pill">{t('overview.notes.saveFailed')}</div> : null}

      <div className="dash-notes-stream">
        {(notes ?? []).map((note) => {
          const editing = editingId === note.id
          return (
            <article key={note.id} className="dash-note-card">
              <div className="dash-note-card-head">
                <div className="dash-note-card-author">
                  <NoteAvatar name={note.authorName} url={note.authorAvatarUrl} />
                  <span className="dash-note-author-name">{note.authorName || t('overview.notes.unnamed')}</span>
                  {note.isMine ? <span className="dash-note-you-pill">{t('overview.notes.you')}</span> : null}
                  <span className="dash-note-dot">·</span>
                  <time className="dash-note-time" dateTime={note.createdAt} title={`${longDate(note.createdAt)} · ${clockTime(note.createdAt)}`}>
                    {relativeNoteTime(note.createdAt)} · {longDate(note.createdAt)}
                  </time>
                  {note.updatedAt !== note.createdAt ? <span className="dash-note-edited">({t('overview.notes.edited')})</span> : null}
                </div>

                {!editing && note.canManage ? (
                  <div className="dash-note-card-actions">
                    {deletingId === note.id ? (
                      <div className="dash-note-confirm-delete">
                        <span>{t('overview.notes.deleteConfirm')}</span>
                        <button type="button" className="dash-note-btn-danger" aria-label={t('overview.notes.delete')} onClick={() => { void remove(note.id) }}><IconCheck size={15} /></button>
                        <button type="button" className="dash-note-btn-ghost" aria-label={t('overview.notes.cancel')} onClick={() => setDeletingId(null)}><IconX size={15} /></button>
                      </div>
                    ) : (
                      <>
                        <button type="button" className="dash-note-action-btn" aria-label={t('overview.notes.edit')} onClick={() => { setEditingId(note.id); setEditText(note.body); setDeletingId(null) }}><IconPencil size={16} /></button>
                        <button type="button" className="dash-note-action-btn is-danger" aria-label={t('overview.notes.delete')} onClick={() => setDeletingId(note.id)}><IconTrash size={16} /></button>
                      </>
                    )}
                  </div>
                ) : null}
              </div>

              {editing ? (
                <div className="dash-note-edit-area">
                  <textarea
                    className="dash-note-edit-textarea"
                    value={editText}
                    maxLength={NOTE_TEXT_MAX}
                    rows={3}
                    autoFocus
                    onChange={(event) => setEditText(event.target.value)}
                    onKeyDown={(event) => {
                      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') { event.preventDefault(); void saveEdit(note.id) }
                      if (event.key === 'Escape') setEditingId(null)
                    }}
                  />
                  <div className="dash-note-edit-bar">
                    <span className="dash-notes-kbd-hint">{t('overview.notes.editHint', { key: COMMAND_KEY })}</span>
                    <div className="dash-note-edit-btns">
                      <button type="button" className="dash-notes-btn-secondary" onClick={() => setEditingId(null)}>{t('overview.notes.cancel')}</button>
                      <button type="button" className="dash-notes-submit-btn" disabled={!editText.trim() || saving} onClick={() => { void saveEdit(note.id) }}>{t('overview.notes.save')}</button>
                    </div>
                  </div>
                </div>
              ) : <div className="dash-note-card-body">{note.body}</div>}
            </article>
          )
        })}
        {(notes ?? []).length === 0 ? (
          <div className="dash-bk-notes-empty">
            <span className="dash-widget-glass-icon" aria-hidden="true"><IconNote size={14} /></span>
            <span><strong>{t('bookings.notes.emptyTitle')}</strong><small>{t('bookings.notes.emptyBody')}</small></span>
          </div>
        ) : null}
      </div>
    </div>
  )
}

/**
 * El menú del panel: lo que se hace CON la cita, no dentro de ella.
 *
 * Sólo aparecen las acciones que se pueden llevar a cabo: sin correo no hay
 * «escribir», y sin servicio vivo no hay a dónde ir. Un menú con la mitad de sus
 * entradas apagadas enseña lo que no se puede hacer, que no es lo que se viene a
 * buscar aquí.
 */
function BookingPanelMenu({ booking, onOpenService, onOpenContact }: {
  booking: Booking
  onOpenService?: (() => void) | undefined
  onOpenContact?: (() => void) | undefined
}) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const close = (event: PointerEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', close)
    return () => document.removeEventListener('pointerdown', close)
  }, [open])

  const email = booking.customerEmail.trim()
  const phone = booking.customerPhone.trim()

  return (
    <div className="dash-bk-bar-menu" ref={boxRef}>
      <button
        type="button"
        className="dash-bk-bar-btn"
        aria-label={t('bookings.detail.moreActions')}
        data-tooltip={t('bookings.detail.moreActions')}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <IconDots size={17} />
      </button>
      {open ? (
        <div className="dash-bk-bar-pop" role="menu">
          {email ? (
            <a role="menuitem" href={`mailto:${email}`}><IconMail size={14} /> {t('bookings.detail.emailCustomer')}</a>
          ) : null}
          {phone ? (
            <a role="menuitem" href={`tel:${phone}`}><IconPhone size={14} /> {t('bookings.detail.call')}</a>
          ) : null}
          {email ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                void navigator.clipboard?.writeText(email)
                setCopied(true)
                window.setTimeout(() => setCopied(false), 1400)
              }}
            >
              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />} {t(copied ? 'bookings.detail.copied' : 'bookings.detail.copyEmail')}
            </button>
          ) : null}
          {onOpenContact ? (
            <button type="button" role="menuitem" onClick={() => { setOpen(false); onOpenContact() }}>
              <IconUser size={14} /> {t('bookings.detail.viewContact')}
            </button>
          ) : null}
          {onOpenService ? (
            <button type="button" role="menuitem" onClick={() => { setOpen(false); onOpenService() }}>
              <IconCalendarEvent size={14} /> {t('bookings.detail.viewService')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export function BookingDetail({ projectId, token, booking, image, capacity, team, onClose, onChange, onOpenContact, onOpenService }: {
  projectId: string
  token: string
  booking: Booking
  /**
   * La portada del servicio, en 16:9.
   *
   * Llega hecha, como en la ficha de una clase: quien abre esto ya tiene el
   * catálogo, y pedirlo desde dentro sería una consulta por cada cita que se
   * mira. Sin ella queda el icono, que es lo que hay en un servicio sin foto.
   */
  image?: string | undefined
  /** El cupo del servicio. Es lo que dice si la cita es individual o de grupo. */
  capacity?: number | undefined
  /**
   * El equipo, para decir con quién es y poder cambiarlo (si esa persona no
   * vino, por ejemplo). Sólo cuenta con más de una persona activa: con una
   * es obvio y no hay a quién cambiar.
   */
  team?: BookingResource[] | undefined
  onClose: () => void
  onChange: (booking: Booking) => void
  onOpenContact?: ((identity: string) => void) | undefined
  /** Abrir el servicio de esta cita. Ausente cuando ya se está dentro de él. */
  onOpenService?: ((offerId: string) => void) | undefined
}) {
  const { showToast } = useToast()
  const { t } = useI18n()
  const [saving, setSaving] = useState('')
  const [order, setOrder] = useState<OrderDetail | null>(null)
  const [paymentLoading, setPaymentLoading] = useState(Boolean(booking.orderId))
  const [paymentError, setPaymentError] = useState(false)
  const [tab, setTab] = useState<'form' | 'events' | 'note'>('events')
  const [orderOpen, setOrderOpen] = useState(false)
  const [rescheduleOpen, setRescheduleOpen] = useState(false)
  /* Un solo camino a la ficha del contacto: lo usan el botón de la esquina, el
     nombre y el correo. Con tres copias de la misma línea, cambiar cómo se
     identifica un contacto habría que acordarse de hacerlo tres veces. */
  /* Los nombres de los acompañantes que dejó al reservar (la respuesta `__guests`). */
  const guestNames = booking.customFields.find((answer) => answer.name === '__guests')?.value?.trim() ?? ''
  const openContact = booking.customerEmail && onOpenContact
    ? () => onOpenContact(`email:${booking.customerEmail.trim().toLocaleLowerCase()}`)
    : null
  const tabsRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const [closing, setClosing] = useState(false)

  const requestClose = useCallback(() => {
    if (saving || closing) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      onClose()
      return
    }
    setClosing(true)
  }, [closing, onClose, saving])

  useEffect(() => {
    if (!closing) return
    // La salida usa el mismo plazo corto de las tarjetas móviles del CMS.
    const timer = window.setTimeout(onClose, 240)
    return () => window.clearTimeout(timer)
  }, [closing, onClose])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !saving && !rescheduleOpen) requestClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [requestClose, rescheduleOpen, saving])

  useEffect(() => {
    if (!booking.orderId) { setOrder(null); setPaymentLoading(false); return }
    let active = true
    setPaymentLoading(true); setPaymentError(false)
    getOrder(projectId, booking.orderId, token)
      .then((result) => { if (active) setOrder(result) })
      .catch(() => { if (active) setPaymentError(true) })
      .finally(() => { if (active) setPaymentLoading(false) })
    return () => { active = false }
  }, [booking.orderId, projectId, token])

  const patch = async (body: Parameters<typeof updateBooking>[2], field: string) => {
    setSaving(field)
    try {
      onChange(await updateBooking(projectId, booking.id, body, token))
      showToast(t('bookings.detail.saved'))
    } catch (error) {
      showToast(bookingError(error), 'error')
    } finally {
      setSaving('')
    }
  }

  /* Dónde va la pastilla. Se remide al cambiar de pestaña y al cambiar de
     tamaño la barra, que se estrecha con el diálogo. */
  useTabThumb(tabsRef, tab)

  const past = isPast(booking)
  const stripe = order?.stripe ?? null
  const hours = allDayCount(booking)
    ? t('bookings.allDay')
    : t('bookings.detail.hoursRange', { from: timeIn(booking.startsAt, booking.timezone), to: timeIn(booking.endsAt, booking.timezone) })
  /*
   * Individual o grupal, según el CUPO del servicio y no el asiento.
   *
   * El asiento decía «Individual» para el primero de una clase de ocho, porque
   * su índice es cero igual que el de una cita de una persona. Con el cupo no
   * hay ambigüedad; y en un grupo se dice además cuál de los lugares es.
   */
  const modeLabel = (capacity ?? 1) > 1
    ? t('bookings.detail.modeGroup', { seat: booking.seatIndex + 1 })
    : t('bookings.detail.modeSingle')
  const canReschedule = Boolean(booking.offerId) && !past && (booking.status === 'held' || booking.status === 'confirmed')
  return createPortal(
    <div
      className={`dash-modal-backdrop${closing ? ' is-closing' : ''}`}
      onPointerDown={(event) => { if (event.target === event.currentTarget && !saving) requestClose() }}
    >
      <section
        className={`dash-bk-dialog${closing ? ' is-closing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-detail-title"
        onWheel={(event) => {
          if (bodyRef.current && !bodyRef.current.contains(event.target as Node)) {
            bodyRef.current.scrollTop += event.deltaY
          }
        }}
      >
        {/* La barra del panel, como la del CMS: cerrar a la izquierda y el resto
            de acciones en el menú. Van aquí y no repartidas por la ficha porque
            son acciones del PANEL —cerrarlo, copiar su enlace, borrar la cita—,
            no de ninguno de sus apartados. */}
        <div className="dash-bk-bar">
          <button type="button" className="dash-bk-bar-btn" aria-label={t('bookings.detail.closePanel')} data-tooltip={t('bookings.detail.closePanel')} onClick={requestClose}>
            <IconChevronsRight size={17} />
          </button>
          <span className="dash-bk-bar-spacer" />
          <BookingPanelMenu
            booking={booking}
            {...(onOpenService && booking.offerId ? { onOpenService: () => onOpenService(booking.offerId as string) } : {})}
            {...(onOpenContact && booking.customerEmail ? { onOpenContact: () => onOpenContact(`email:${booking.customerEmail.trim().toLocaleLowerCase()}`) } : {})}
          />
        </div>
        {/* La persona y el servicio son la identidad de la ficha. La fecha vive
            justo debajo, como el dato operativo principal. */}
        <header className="dash-bk-head">
          {image
            ? <img className="dash-bk-shot" src={image} alt="" />
            : <span className="dash-widget-glass-icon dash-bk-avatar" aria-hidden="true"><IconUser size={20} /></span>}
          <div className="dash-bk-head-copy">
            <span className="dash-bk-eyebrow">{t('bookings.detail.eyebrow')}</span>
            <h2 id="booking-detail-title">{booking.customerName || t('bookings.detail.unnamed')}</h2>
            {/* El servicio, con su icono y pulsable: desde una cita se va a
                mirar «cómo está configurado esto» más de lo que parece, y hasta
                ahora había que salir a la lista y buscarlo por el nombre.
                Se queda en texto si no hay a dónde ir —servicio borrado, o ya
                estamos dentro de él—, que es cuando el enlace mentiría. */}
            {booking.offerId && onOpenService ? (
              <p>
                <button
                  type="button"
                  className="dash-booking-service-link dash-bk-service"
                  onClick={() => onOpenService(booking.offerId as string)}
                >
                  <IconCalendarEvent size={14} aria-hidden="true" />
                  {booking.offerTitle || t('bookings.serviceFallback')}
                </button>
                <span className="dash-bk-mode">{modeLabel}</span>
              </p>
            ) : (
              <p className="dash-bk-service-plain">
                <IconCalendarEvent size={14} aria-hidden="true" />
                {booking.offerTitle || t('bookings.detail.serviceUnavailable')}
                <span className="dash-bk-mode">{modeLabel}</span>
              </p>
            )}

          </div>

          {/* El estado, enfrente del nombre: es lo único de la ficha que se
              toca, y en la barra del panel —entre cerrar y el menú— parecía una
              acción del panel y no de la cita. Con su rótulo encima, como los
              recuadros de fecha y hora, para que el chip diga de qué es. */}
          <div className="dash-bk-state-row">
            <span className="dash-bk-state-label">{t('bookings.detail.statusLabel')}</span>
            <BookingStatusSelect
              booking={booking}
              busy={saving === 'status'}
              onChange={(status) => void patch({ status }, 'status')}
            />
          </div>

          {/* El aviso cuelga del estado, cruzando la cabecera: dice qué falta
              hacer con el mando que tiene justo encima —marcar asistencia o
              no—, así que se lee como su pie y no como una nota suelta. */}
          {past && (booking.status === 'held' || booking.status === 'confirmed')
            ? (
              <p className="dash-bk-past" role="status">
                <IconAlertTriangle size={14} aria-hidden="true" />
                {t('bookings.detail.pastNotice')}
              </p>
            )
            : null}
        </header>

        {/*
          El «cuándo» de la cita: un bloque con cabecera, como Cliente y Cobro.

          Fueron dos tarjetas —día y hora— y el botón vivía dentro de la del día
          aunque cambia las dos cosas. Con una cabecera propia, el título nombra
          el bloque entero y el botón se va al extremo: la acción es del bloque,
          no de una mitad.

          Debajo, cada dato en su propia tarjeta. Y con tarjetas separadas los
          rótulos vuelven: dentro de una caja partida sobraban —el título de
          arriba ya los decía— pero dos cajas sueltas tienen que saber decir cada
          una qué guarda.
        */}
        <section className="dash-bk-overview" aria-label={t('bookings.detail.overviewAria')}>
          <div className="dash-bk-when">
            <div className="dash-bk-when-head">
              <span className="dash-bk-when-title">
                {/* El mismo chip de cristal que encabeza Cliente y Cobro: son
                    bloques de la misma ficha y uno sin marca se leía aparte. */}
                <span className="dash-widget-glass-icon dash-settings-head-icon dash-bk-block-icon" aria-hidden="true">
                  <IconCalendarEvent size={14} />
                </span>
                {t('bookings.detail.when')}
              </span>
              {canReschedule ? (
                <button type="button" className="dash-secondary-btn dash-bk-reschedule-button" onClick={() => setRescheduleOpen(true)}>
                  <IconPencil size={13} /> {t('bookings.detail.reschedule')}
                </button>
              ) : null}
            </div>
            <dl className="dash-bk-facts">
              {/* En dos renglones a propósito —entera se partía por donde tocara,
                  a veces dejando «del» solo al final, y el corte cambiaba con el
                  ancho— y con la FECHA arriba: es el dato que se viene a buscar.
                  El día de la semana estaba encima y leía primero «Friday», que
                  es lo que uno deduce de la fecha, no al revés. */}
              <div>
                <dt>{t('bookings.detail.date')}</dt>
                <dd>
                  {longDate(booking.startsAt, booking.timezone)}
                  <small>{weekdayIn(booking.startsAt, booking.timezone)}</small>
                </dd>
              </div>
              {/* La duración vive bajo la hora y no en su propia columna: es la
                  segunda mitad del mismo dato —de cuándo a cuándo— y separarla
                  obligaba a mirar dos sitios para saber cuánto dura. En palabras
                  porque aquí hay sitio: «1 h 30 min» es para una tabla. */}
              <div>
                <dt>{t('bookings.detail.time')}</dt>
                <dd>{hours}<small>{longDurationLabel(booking, t)}</small></dd>
              </div>
            </dl>
          </div>
        </section>

        <div className="dash-bk-body" ref={bodyRef}>
          <Block title={t('bookings.detail.client')} variant="client" icon={<IconUser size={14} aria-hidden="true" />} aside={openContact
            ? <button type="button" className="dash-secondary-btn dash-bk-contact-link" onClick={openContact}><IconUser size={13} /> {t('bookings.detail.viewContact')}</button>
            : null}>
            <dl className="dash-bk-list">
              {/* El nombre y el correo llevan a su ficha, no sólo el botón de la
                  esquina: al leer «Juan» lo que se quiere es ver quién es Juan, y
                  obligar a buscar el botón para eso es un paso de más.
                  Hace falta correo porque es lo que identifica al contacto: una
                  cita sin correo no tiene ficha a la que ir. */}
              <Row label={t('bookings.detail.name')}>
                {booking.customerName
                  ? openContact
                    ? <button type="button" className="dash-bk-contact-open" onClick={openContact}>{booking.customerName}</button>
                    : booking.customerName
                  : <em>{t('bookings.noName')}</em>}
              </Row>
              {/* Los acompañantes, debajo de quien reservó: así se reconoce el grupo de un vistazo. */}
              {guestNames ? <Row label={t('bookings.detail.guests')}>{guestNames}</Row> : null}
              <Row label={t('bookings.detail.email')}>
                {booking.customerEmail
                  ? openContact
                    ? <button type="button" className="dash-bk-contact-open" onClick={openContact}>{booking.customerEmail}</button>
                    : <a href={`mailto:${booking.customerEmail}`}>{booking.customerEmail}</a>
                  : <em>{t('bookings.noEmail')}</em>}
              </Row>
              {/* El número agrupado para leerse; el enlace sigue llevando lo que
                  hay en la base, que es lo que quiere el marcador. */}
              <Row label={t('bookings.detail.phone')}>{booking.customerPhone ? <a href={`tel:${booking.customerPhone}`}>{prettyPhone(booking.customerPhone)}</a> : <em>{t('bookings.noPhone')}</em>}</Row>
              {(booking.partySize ?? 1) > 1 ? <Row label={t('bookings.detail.people')}>{t('bookings.detail.peopleCount', { count: booking.partySize ?? 1 })}</Row> : null}
              {team && team.filter((row) => row.active).length > 1 ? (
                <Row label={t('bookings.detail.withWhom')}>
                  <ArtistPicker
                    team={team}
                    currentId={booking.resourceId}
                    disabled={booking.status === 'cancelled' || saving === 'resource'}
                    onPick={(resourceId) => void patch({ resourceId }, 'resource')}
                  />
                </Row>
              ) : null}
            </dl>
          </Block>

          <Block
            title={t('bookings.detail.payment')}
            variant="payment"
            icon={<IconReceipt size={14} aria-hidden="true" />}
            /* El importe estaba aquí y también en la línea de estado: en un
               bloque de cuatro renglones, la misma cifra dos veces se lee como
               dos cobros. Se queda abajo, junto a su estado, y este sitio lo
               ocupa lo único que faltaba: la salida al cobro entero. */
            aside={order ? (
              <button type="button" className="dash-secondary-btn dash-bk-more" onClick={() => setOrderOpen(true)}>
                <IconReceipt size={13} aria-hidden="true" /> {t('bookings.detail.viewFull')}
              </button>
            ) : null}
          >
            {paymentLoading ? (
              <p className="dash-bk-muted"><IconLoader2 className="dash-spin" size={15} /> {t('bookings.detail.loadingPayment')}</p>
            ) : order ? (
              <>
                {/* El importe y su estado en la misma línea: son la respuesta a
                    «¿está pagado?», y separados en dos filas obligaban a juntar
                    la cifra de arriba con la palabra de abajo. */}
                <dl className="dash-bk-list">
                  <Row label={t('bookings.detail.statusLabel')}>
                    <span className="dash-bk-paid">
                      <strong>{order.formatted}</strong>
                      <span className={`dash-offer-status ${orderStatusTone(order.status)}`}>{paymentStatus(order, t)}</span>
                    </span>
                  </Row>
                  <Row label={t('bookings.detail.method')}>{stripe?.cardLast4 ? `${stripe.cardBrand ? `${stripe.cardBrand.toUpperCase()} ` : ''}•••• ${stripe.cardLast4}` : t('bookings.detail.viaStripe')}</Row>
                  {/* Cuándo entró el dinero. No es lo mismo que cuándo es la
                      cita —de eso va todo el bloque de arriba— y en un cobro
                      discutido es la primera fecha que se busca. */}
                  <Row label={t('bookings.detail.paidOn')}>{longDate(order.createdAt)}</Row>
                </dl>

              </>
            ) : (
              /* Los tres vacíos de este bloque no dicen lo mismo y por eso no
                 llevan el mismo icono: «no hay cobro» es un hecho, «falta
                 pagar» es una espera y «falló» es un problema. Con el mismo
                 símbolo para los tres había que leer el texto para saber en
                 cuál de los tres estás. */
              <div className="dash-bk-empty">
                <span className={`dash-bk-empty-icon${paymentError ? ' is-bad' : ''}`} aria-hidden="true">
                  {paymentError ? <IconAlertTriangle size={19} />
                    : booking.status === 'held' ? <IconHourglass size={19} />
                      : <IconCreditCardOff size={19} />}
                </span>
                <strong>{paymentError ? t('bookings.detail.pay.failedTitle')
                  : booking.status === 'held' ? t('bookings.detail.pay.pendingTitle')
                    : t('bookings.detail.pay.noneTitle')}</strong>
                <span>{paymentError ? t('bookings.detail.pay.failedBody')
                  : booking.status === 'held' ? t('bookings.detail.pay.pendingBody')
                    : t('bookings.detail.pay.noneBody')}</span>
              </div>
            )}
          </Block>

          {/*
            Formulario, proceso y notas, en pestañas.
            Los tres cuentan cosas distintas de la misma cita y ninguno se mira a
            la vez que otro: apilados ocupaban tres bloques de los que dos
            sobraban siempre. El historial abre primero y las notas comparten
            sitio en vez de una tarjeta propia.
          */}
          <section className="dash-bk-tabs-block">
            <div className="dash-bk-tabs" role="tablist" aria-label={t('bookings.detail.tabsAria')} ref={tabsRef}>
              {/* La pastilla se mide después de repartir el ancho disponible:
                  así sigue exactamente al botón activo incluso si cambia el
                  tamaño del diálogo. Va antes que los botones en el DOM y por
                  debajo, para que el texto se lea encima de ella. */}
              <span className="dash-bk-tabs-thumb" aria-hidden="true" />
              {([
                ['form', 'bookings.detail.tab.form', <IconClipboardList size={13} key="f" />],
                ['events', 'bookings.detail.tab.events', <IconHistory size={13} key="e" />],
                ['note', 'bookings.detail.tab.note', <IconNote size={13} key="n" />],
              ] as const).map(([value, labelKey, icon]) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={tab === value}
                  className={tab === value ? 'is-active' : ''}
                  onClick={() => setTab(value)}
                >
                  {icon} {t(labelKey)}
                </button>
              ))}
            </div>

            <div className="dash-bk-tabs-body">
              {tab === 'form' ? (
                booking.customFields.some((answer) => answer.name !== '__guests') || booking.customerNote ? (
                  /* Se lee la etiqueta GUARDADA en la respuesta, no la que tenga
                     hoy el servicio: si la pregunta se renombró o se borró
                     después, esto sigue contando lo que se le preguntó a esta
                     persona. */
                  <dl className="dash-bk-answers">
                    {booking.customFields.filter((answer) => answer.name !== '__guests').map((answer) => (
                      <div key={answer.name}>
                        <dt>{answer.label}</dt>
                        <dd>{answer.value || <em>{t('bookings.detail.noAnswer')}</em>}</dd>
                      </div>
                    ))}
                    {booking.customerNote ? (
                      <div>
                        <dt>{t('bookings.detail.customerNote')}</dt>
                        <dd>{booking.customerNote}</dd>
                      </div>
                    ) : null}
                  </dl>
                ) : <p className="dash-bk-muted">{t('bookings.detail.noQuestions')}</p>
              ) : tab === 'events' ? (
                <BookingTimeline projectId={projectId} token={token} bookingId={booking.id} />
              ) : (
                <BookingNotes projectId={projectId} token={token} bookingId={booking.id} />
              )}
            </div>
          </section>

        </div>
      </section>
      {/* La ficha del cobro, encima de la de la cita: se consulta y se cierra
          sin perder dónde estabas. */}
      {orderOpen && order ? (
        <OrderDetailDialog projectId={projectId} token={token} order={order} onClose={() => setOrderOpen(false)} />
      ) : null}
      {rescheduleOpen && booking.offerId ? (
        <RescheduleBookingDialog
          projectId={projectId}
          token={token}
          booking={booking}
          onClose={() => setRescheduleOpen(false)}
          onRescheduled={(updated) => { onChange(updated); setRescheduleOpen(false); setTab('events') }}
        />
      ) : null}
    </div>,
    document.body,
  )
}
